import { Fragment, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import { BarChart3, FileDown } from "lucide-react";
import { toast } from "sonner";
import { downloadCertificatePdf } from "@/lib/pdf";

/**
 * SchoolStatsPrint — إحصائية شاملة للمدرسة: عدد الطلاب في كل صف، عدد
 * فصول كل صف، إجمالي كل صف، ثم إجمالي المدرسة الكلي — بنفس تصميم
 * قوائم الفصول، وتصدير PDF.
 */
export default function SchoolStatsPrint() {
  const { ctx, meta } = useApp();
  const grades = meta?.grades_by_stage?.[ctx.stage] || [];
  const [settings, setSettings] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      // نجيب كل طلاب المرحلة/العام الحالي (بلا تحديد صف)، ونجمّعهم محليًا
      // حسب الصف ثم الفصل تحديدًا (وليس عدد الفصول فقط)، للحصول على تفصيل
      // دقيق: كل فصل وعدد طلابه، ثم إجمالي كل صف، ثم إجمالي المدرسة.
      const { data: students } = await api.get("/students", {
        params: { stage: ctx.stage, year: ctx.year },
      });

      // byGrade[grade][className] = عدد الطلاب في هذا الفصل تحديدًا داخل
      // هذا الصف — مفتاح مركَّب (صف + فصل) يمنع أي احتساب مزدوج أو خلط
      // بين فصول بنفس الاسم في صفوف مختلفة (كل صف له مجموعة فصول مستقلة).
      const byGrade = {};
      students.forEach((s) => {
        if (!byGrade[s.grade]) byGrade[s.grade] = {};
        const cls = s.class || "بدون فصل محدَّد";
        byGrade[s.grade][cls] = (byGrade[s.grade][cls] || 0) + 1;
      });

      const rows = grades
        .filter((g) => byGrade[g] && Object.keys(byGrade[g]).length > 0)
        .map((g) => {
          const classes = Object.entries(byGrade[g])
            .sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true }))
            .map(([className, count]) => ({ className, count }));
          const gradeTotal = classes.reduce((sum, c) => sum + c.count, 0);
          return { grade: g, classes, gradeTotal };
        });

      // إجمالي المدرسة: مجموع طلاب كل الصفوف. عدد فصول المدرسة: مجموع عدد
      // الفصول الفعلية داخل كل صف على حدة (وليس عدًّا كليًا لأسماء فصول
      // فريدة عبر المدرسة كلها) — بالضبط كما هو مطلوب: فصل "1/1" في الصف
      // الأول وفصل "1/1" في الصف الثاني (لو تكرر نفس الاسم) يُحسَبان
      // فصلَين مختلفين تمامًا، لا فصلًا واحدًا.
      const schoolTotalStudents = rows.reduce((sum, r) => sum + r.gradeTotal, 0);
      const schoolTotalClasses = rows.reduce((sum, r) => sum + r.classes.length, 0);

      if (!rows.length) {
        toast.error("لا يوجد طلاب مسجَّلون بعد لهذه المرحلة");
        return;
      }

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.left = "-99999px";
      container.style.top = "0";
      container.style.direction = "rtl";
      container.style.background = "#ffffff";
      document.body.appendChild(container);

      const wrap = document.createElement("div");
      wrap.style.width = "794px";
      wrap.style.padding = "20px";
      wrap.style.background = "#ffffff";
      container.appendChild(wrap);

      const { createRoot } = await import("react-dom/client");
      const root = createRoot(wrap);
      await new Promise((resolve) => {
        root.render(
          <SchoolStatsSheet school={settings} stage={ctx.stage} year={ctx.year}
                            rows={rows} totalStudents={schoolTotalStudents}
                            totalClasses={schoolTotalClasses} />
        );
        document.fonts.ready.then(() => setTimeout(resolve, 150));
      });
      await downloadCertificatePdf(wrap, `إحصائية-المدرسة-${ctx.stage}.pdf`, { alignTop: true });
      container.remove();
      toast.success("تم توليد إحصائية المدرسة بنجاح");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="schoolstats-page">
      <PageHeader
        icon={BarChart3}
        overline="التقارير والشهادات"
        title="إحصائية المدرسة"
        subtitle="عدد الطلاب والفصول في كل صف، وإجمالي المدرسة الكلي، جاهزة للطباعة."
      />

      <div className="rounded-xl border border-border bg-white p-6 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          تُبنى الإحصائية من بيانات الطلاب المسجَّلة حاليًا في المرحلة والعام الدراسي المختارَين من الشريط العلوي.
        </div>
        <Button onClick={generate} disabled={generating} className="h-10" data-testid="stats-generate">
          <FileDown className="h-4 w-4 me-1.5" /> {generating ? "جارٍ التوليد…" : "تنزيل PDF"}
        </Button>
      </div>
    </div>
  );
}

/** ورقة إحصائية المدرسة — للطباعة/التصدير فقط. */
function SchoolStatsSheet({ school, stage, year, rows, totalStudents, totalClasses }) {
  return (
    <div className="mx-auto max-w-4xl bg-white border-4 border-primary/70 rounded-xl p-8 shadow-sm">
      <div className="border-b-2 border-primary/50 pb-4 text-center space-y-1 relative">
        {school?.ministry_logo && (
          <img src={school.ministry_logo} alt="شعار الوزارة"
               className="absolute top-0 start-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        {school?.school_logo && (
          <img src={school.school_logo} alt="شعار المدرسة"
               className="absolute top-0 end-0 h-16 w-16 object-contain" crossOrigin="anonymous" />
        )}
        <div className="text-primary font-display text-xl font-bold">{school?.ministry_name || "وزارة التعليم"}</div>
        {school?.education_dept && <div className="text-primary font-medium">{school.education_dept}</div>}
        {school?.office_name && <div className="text-primary font-medium">{school.office_name}</div>}
        <div className="text-primary font-display text-lg font-bold">{school?.school_name || "المدرسة"}</div>
      </div>

      <div className="mt-6 text-center">
        <h2 className="font-display text-lg font-bold text-primary">إحصائية المدرسة</h2>
        <div className="mt-2 text-sm">{stage}</div>
        <div className="mt-1 text-xs text-muted-foreground">العام الدراسي: {year}</div>
      </div>

      <table className="w-full mt-6 text-sm border-collapse">
        <thead>
          <tr className="bg-primary text-white">
            <th className="px-2 py-2 text-center border border-primary w-12">م</th>
            <th className="px-2 py-2 text-start border border-primary">البيان</th>
            <th className="px-2 py-2 text-center border border-primary w-32">عدد الطلاب</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Fragment key={r.grade}>
              <tr className="bg-slate-100">
                <td colSpan={3} className="px-2 py-2 border border-slate-200 font-bold text-primary">
                  {r.grade}
                </td>
              </tr>
              {r.classes.map((c, ci) => (
                <tr key={c.className}>
                  <td className="px-2 py-1.5 border border-slate-200 text-center text-muted-foreground">{ci + 1}</td>
                  <td className="px-2 py-1.5 border border-slate-200">الفصل {c.className}</td>
                  <td className="px-2 py-1.5 border border-slate-200 text-center tabular-nums">{c.count}</td>
                </tr>
              ))}
              <tr className="bg-primary/5 font-semibold">
                <td colSpan={2} className="px-2 py-1.5 border border-slate-200">إجمالي {r.grade}</td>
                <td className="px-2 py-1.5 border border-slate-200 text-center tabular-nums">{r.gradeTotal}</td>
              </tr>
            </Fragment>
          ))}
          <tr className="bg-primary/10 font-bold">
            <td colSpan={2} className="px-2 py-2 border border-primary/30">إجمالي طلاب المدرسة</td>
            <td className="px-2 py-2 border border-primary/30 text-center tabular-nums">{totalStudents}</td>
          </tr>
          <tr className="bg-primary/10 font-bold">
            <td colSpan={2} className="px-2 py-2 border border-primary/30">عدد فصول المدرسة</td>
            <td className="px-2 py-2 border border-primary/30 text-center tabular-nums">{totalClasses}</td>
          </tr>
        </tbody>
      </table>

      <div className="mt-10 grid grid-cols-3 text-center text-sm">
        <div />
        <div>
          <div className="text-muted-foreground text-xs mb-8">الختم</div>
          <div className="mx-auto h-20 w-20 rounded-full border-2 border-dashed border-slate-300" />
        </div>
        <div>
          <div className="text-primary font-semibold">
            {school?.field_labels?.school_manager || "مدير المدرسة"}
          </div>
          <div className="font-semibold mt-8">{school?.school_manager || "—"}</div>
        </div>
      </div>
    </div>
  );
}
